
import { supabase } from '@/integrations/supabase/client';

export interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

export const sitemapApi = {
  /**
   * Generate dynamic sitemap URLs from database content
   */
  generateSitemapUrls: async (): Promise<SitemapUrl[]> => {
    const baseUrl = 'https://app.recut.art';
    const urls: SitemapUrl[] = [];
    
    // Static pages with high priority
    const staticPages: SitemapUrl[] = [
      {
        loc: `${baseUrl}/`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'daily',
        priority: 1.0
      },
      {
        loc: `${baseUrl}/channels`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.9
      },
      {
        loc: `${baseUrl}/clips`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.9
      },
      {
        loc: `${baseUrl}/playlists`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.9
      },
      {
        loc: `${baseUrl}/derivatives`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'daily',
        priority: 0.8
      },
      {
        loc: `${baseUrl}/creators`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.7
      },
      {
        loc: `${baseUrl}/LamourCestSurcote`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: 'weekly',
        priority: 0.9
      }
    ];
    
    urls.push(...staticPages);
    
    try {
      // Fetch channels
      const { data: channels } = await supabase
        .from('channels')
        .select('id, created_at')
        .order('created_at', { ascending: false })
        .limit(1000);
      
      if (channels) {
        channels.forEach(channel => {
          urls.push({
            loc: `${baseUrl}/channel/${channel.id}`,
            lastmod: channel.created_at.split('T')[0],
            changefreq: 'weekly',
            priority: 0.7
          });
        });
      }
      
      // Fetch playlists
      const { data: playlists } = await supabase
        .from('playlists')
        .select('id, created_at')
        .order('created_at', { ascending: false })
        .limit(1000);
      
      if (playlists) {
        playlists.forEach(playlist => {
          urls.push({
            loc: `${baseUrl}/playlist/${playlist.id}`,
            lastmod: playlist.created_at.split('T')[0],
            changefreq: 'weekly',
            priority: 0.6
          });
        });
      }
      
      // Fetch clips
      const { data: clips } = await supabase
        .from('clips')
        .select('id, created_at')
        .order('created_at', { ascending: false })
        .limit(2000);
      
      if (clips) {
        clips.forEach(clip => {
          urls.push({
            loc: `${baseUrl}/clip/${clip.id}`,
            lastmod: clip.created_at.split('T')[0],
            changefreq: 'monthly',
            priority: 0.5
          });
        });
      }
      
      // Fetch approved derivatives
      const { data: derivatives } = await supabase
        .from('derivatives')
        .select('id, submitted_date')
        .eq('status', 'approved')
        .order('submitted_date', { ascending: false })
        .limit(1000);
      
      if (derivatives) {
        derivatives.forEach(derivative => {
          urls.push({
            loc: `${baseUrl}/derivative/${derivative.id}`,
            lastmod: derivative.submitted_date.split('T')[0],
            changefreq: 'monthly',
            priority: 0.6
          });
        });
      }
      
      // Fetch creator profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, created_at')
        .not('username', 'is', null)
        .order('created_at', { ascending: false })
        .limit(500);
      
      if (profiles) {
        profiles.forEach(profile => {
          urls.push({
            loc: `${baseUrl}/creator-profile/${profile.id}`,
            lastmod: profile.created_at.split('T')[0],
            changefreq: 'weekly',
            priority: 0.4
          });
        });
      }
      
    } catch (error) {
      console.error('Error generating sitemap URLs:', error);
    }
    
    return urls;
  },
  
  /**
   * Generate XML sitemap
   */
  generateXmlSitemap: async (): Promise<string> => {
    const urls = await sitemapApi.generateSitemapUrls();
    
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
    
    urls.forEach(url => {
      xml += '  <url>\n';
      xml += `    <loc>${url.loc}</loc>\n`;
      if (url.lastmod) {
        xml += `    <lastmod>${url.lastmod}</lastmod>\n`;
      }
      if (url.changefreq) {
        xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
      }
      if (url.priority) {
        xml += `    <priority>${url.priority}</priority>\n`;
      }
      xml += '  </url>\n';
    });
    
    xml += '</urlset>';
    
    return xml;
  }
};
